import * as RadixNavigationMenu from "@radix-ui/react-navigation-menu";
import { Slot } from "@radix-ui/react-slot";
import clsx from "clsx";
import {
  forwardRef,
  ElementRef,
  ComponentPropsWithoutRef,
  useCallback,
  useContext,
  ReactElement,
  MouseEvent,
  useState,
} from "react";
import React from "react";

import classes from "./navigation-menu.module.css";

type NavigationMenuContextType = {
  onValueChange?: (value: string) => void;
};

const NavigationMenuContext = React.createContext<NavigationMenuContextType>(
  {},
);

type RootProps = Omit<
  ComponentPropsWithoutRef<typeof RadixNavigationMenu.Root>,
  "value" | "onValueChange"
>;

export const Root = forwardRef<
  ElementRef<typeof RadixNavigationMenu.Root>,
  RootProps
>(({ className = "", ...props }, ref) => {
  const [value, setValue] = useState("");

  return (
    <NavigationMenuContext.Provider value={{ onValueChange: setValue }}>
      <RadixNavigationMenu.Root
        ref={ref}
        className={clsx([classes.root, className])}
        delayDuration={0}
        value={value}
        onValueChange={setValue}
        {...props}
      />
    </NavigationMenuContext.Provider>
  );
});

export const List = forwardRef<
  ElementRef<typeof RadixNavigationMenu.List>,
  ComponentPropsWithoutRef<typeof RadixNavigationMenu.List> & {
    orientation?: "horizontal" | "vertical";
  }
>(({ className = "", orientation = "horizontal", ...props }, ref) => {
  return (
    <RadixNavigationMenu.List
      ref={ref}
      className={clsx([classes.list, className])}
      data-orientation={orientation}
      {...props}
    />
  );
});

export const Item = forwardRef<
  ElementRef<typeof RadixNavigationMenu.Item>,
  ComponentPropsWithoutRef<typeof RadixNavigationMenu.Item>
>(({ className = "", ...props }, ref) => {
  return (
    <RadixNavigationMenu.Item
      ref={ref}
      className={clsx([classes.item, className])}
      {...props}
    />
  );
});

export const DropdownItemToggle = forwardRef<
  ElementRef<typeof RadixNavigationMenu.Trigger>,
  ComponentPropsWithoutRef<typeof RadixNavigationMenu.Trigger>
>(({ className = "", ...props }, ref) => {
  return (
    <RadixNavigationMenu.Trigger
      ref={ref}
      className={clsx([classes.trigger, className])}
      {...props}
    />
  );
});

export const DropdownItemContent = forwardRef<
  ElementRef<typeof RadixNavigationMenu.Content>,
  ComponentPropsWithoutRef<typeof RadixNavigationMenu.Content>
>(({ className = "", ...props }, ref) => {
  const { onPointerDownCapture, ...restProps } = props;

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.stopPropagation();

      if (onPointerDownCapture) {
        onPointerDownCapture(e);
      }
    },
    [onPointerDownCapture],
  );

  return (
    <RadixNavigationMenu.Content
      ref={ref}
      className={clsx([classes.content, className])}
      forceMount
      onPointerDownCapture={handlePointerDown}
      onPointerUpCapture={(e: React.PointerEvent<HTMLDivElement>) =>
        e.stopPropagation()
      }
      {...restProps}
    />
  );
});

type CloseDropdownProps = {
  asChild?: boolean;
  children: ReactElement<{ onClick?: (event: MouseEvent) => void }>;
};

export const CloseDropdown = ({ asChild, children }: CloseDropdownProps) => {
  const { onValueChange } = useContext(NavigationMenuContext);
  const Comp = asChild ? Slot : "button";

  const handleClick = useCallback(
    (e: MouseEvent) => {
      // Call the original onClick if it exists
      if (children.props.onClick) {
        children.props.onClick(e);
      }

      if (onValueChange) {
        onValueChange("");
      }
    },
    [children.props, onValueChange],
  );

  return <Comp onClick={handleClick}>{children}</Comp>;
};

Root.displayName = "NavigationMenu.Root";
List.displayName = "NavigationMenu.List";
Item.displayName = "NavigationMenu.Item";
DropdownItemToggle.displayName = "NavigationMenu.DropdownItemToggle";
DropdownItemContent.displayName = "NavigationMenu.DropdownItemContent";
CloseDropdown.displayName = "NavigationMenu.CloseDropdown";
